import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'commons/hooks';
import { useGetCountry, useGetUserInfo } from 'domains/Auth/hooks';
import { REGISTRATION_VALIDATION_SCHEMA } from './registrationValidationSchema';
import { RegistrationFormValues } from '../types';

const noop = () => undefined;

export const useRegistrationForm = () => {
  const userInfo = useGetUserInfo();
  const storeCountry = useGetCountry();
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);

  const initialFormData: RegistrationFormValues = useMemo(
    () => ({
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      ownerEmail: userInfo.email || '',
      country: storeCountry || '',
      phone: '',
      zipCode: '',
      address: '',
      state: '',
      city: '',
    }),
    [userInfo.firstName, userInfo.lastName, userInfo.email, storeCountry],
  );

  useEffect(() => {
    if (userInfo.id || storeCountry) {
      setIsInitialDataLoading(false);
    }
  }, [userInfo.id, storeCountry]);

  const {
    values,
    errors,
    handleChange: originalHandleChange,
    handleChangeSelect: originalHandleChangeSelect,
    handleBlur,
    handleOnSubmit,
    handleValidations,
    isDirty,
    isValidating,
    setMultipleFieldValues,
    cleanErrors,
  } = useForm<RegistrationFormValues>({
    initialValues: initialFormData,
    onSubmit: noop,
    validationSchema: REGISTRATION_VALIDATION_SCHEMA,
    validateOnBlur: Object.keys(initialFormData) as Array<
      keyof RegistrationFormValues
    >,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    originalHandleChange(e);

    if (Object.keys(errors).length > 0) {
      setTimeout(cleanErrors, 0);
    }
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    originalHandleChangeSelect(e);

    if (Object.keys(errors).length > 0 && e.target.value) {
      setTimeout(cleanErrors, 0);
    }
  };

  return {
    values,
    errors,
    handleChange,
    handleChangeSelect,
    handleBlur,
    handleOnSubmit,
    handleValidations,
    isDirty,
    isValidating,
    setMultipleFieldValues,
    isInitialDataLoading,
  };
};
